import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Tooltip,
  IconButton,
  Typography,
  Avatar,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  PointOfSale as POSIcon,
  Inventory2 as InventarioIcon,
  ManageSearch as GestionIcon,
  ShoppingCart as VentasIcon,
  People as ClientesIcon,
  LocalShipping as ComprasIcon,
  Assessment as ReportesIcon,
  Settings as ConfigIcon,
  ExpandLess,
  ExpandMore,
  Category as ProductosIcon,
  AccountBalance as CajaIcon,
  Receipt as FacturacionIcon,
  ChevronLeft,
  ChevronRight,
  Store as SucursalIcon,
  AdminPanelSettings as AdminIcon,
  ManageAccounts as UsersIcon,
  Business as EmpresaIcon,
  LocalOffer as PromoIcon,
  Storefront as TiendaIcon,
  Tune as CatalogoAlmacenIcon,
  Payment as MetodosPagoIcon,
  DeviceHub as PerifericosIcon,
  PriceChange as ListasPreciosIcon,
  Badge as PerfilIcon,
  CloudUpload as ImportIcon,
  QrCodeScanner as ScanIcon,
  AccountBalance as AccountBalanceIcon,
  DeliveryDining as DomiciliosIcon,
  TwoWheeler as DomiciliarioIcon,
  Restaurant as RestauranteIcon,
  TableRestaurant as MesasIcon,
  MenuBook as RecetasIcon,
  SoupKitchen as CocinaIcon,
  Tune as ModificadoresIcon,
  Tv as TurnosIcon,
  Category as TipoNegocioIcon,
  QrCode2 as MenuVirtualIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch, useNegocio } from '@/hooks';
import { toggleSidebarCollapsed } from '@/store/uiSlice';
import iconImage from '../../assets/Icon.png'; 
import EmpresaActiva from './EmpresaActiva';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 72;

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  permiso?: string;
  roles?: string[];  // si se define, solo se muestra a esos roles
  modulo?: string;   // si se define, solo se muestra cuando la empresa tiene el módulo
}

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/',
  },
  {
    id: 'pos',
    label: 'Punto de Venta',
    icon: <POSIcon />,
    path: '/pos',
    permiso: 'pos:ver',
    modulo: 'POS',
  },
  {
    id: 'restaurante',
    label: 'Restaurante',
    icon: <RestauranteIcon />,
    children: [
      { id: 'cocina', label: 'Pantalla de cocina', icon: <CocinaIcon />, path: '/cocina', modulo: 'COCINA' },
      { id: 'turnos', label: 'Turnero de pedidos', icon: <TurnosIcon />, path: '/turnos', modulo: 'COCINA' },
      { id: 'mesas-config', label: 'Zonas y mesas', icon: <MesasIcon />, path: '/restaurante/mesas', modulo: 'MESAS', roles: ['SuperAdmin', 'Admin', 'Supervisor'] },
      { id: 'recetas', label: 'Recetas y combos', icon: <RecetasIcon />, path: '/restaurante/recetas', modulo: 'RECETAS', roles: ['SuperAdmin', 'Admin', 'Supervisor'] },
      { id: 'modificadores', label: 'Modificadores', icon: <ModificadoresIcon />, path: '/restaurante/modificadores', modulo: 'MODIFICADORES', roles: ['SuperAdmin', 'Admin', 'Supervisor'] },
      { id: 'menu-virtual', label: 'Menú virtual', icon: <MenuVirtualIcon />, path: '/restaurante/menu-virtual', modulo: 'MENU_VIRTUAL', roles: ['SuperAdmin', 'Admin', 'Supervisor'] },
    ],
  },
  {
    id: 'catalogo',
    label: 'Catálogo',
    modulo: 'CATALOGO',
    icon: <ProductosIcon />,
    children: [
      { id: 'productos', label: 'Productos', icon: <ProductosIcon />, path: '/catalogo/productos' },
      { id: 'categorias', label: 'Categorías', icon: <ProductosIcon />, path: '/catalogo/categorias' },
      { id: 'proveedores', label: 'Proveedores', icon: <ComprasIcon />, path: '/catalogo/proveedores' },
      { id: 'productos-campo', label: 'Captura en Campo', icon: <ScanIcon />, path: '/catalogo/productos-campo' },
    ],
  },
  {
    id: 'inventario',
    label: 'Inventario',
    modulo: 'INVENTARIO',
    icon: <InventarioIcon />,
    children: [
      { id: 'gestion-stock', label: 'Gestión de Stock', icon: <GestionIcon />, path: '/inventario/gestion-stock' },
      { id: 'stock', label: 'Existencias', icon: <InventarioIcon />, path: '/inventario/stock' },
      { id: 'movimientos', label: 'Movimientos', icon: <InventarioIcon />, path: '/inventario/movimientos' },
      { id: 'transferencias', label: 'Transferencias', icon: <InventarioIcon />, path: '/inventario/transferencias' },
      { id: 'conteos', label: 'Conteos', icon: <InventarioIcon />, path: '/inventario/conteos' },
      { id: 'lotes', label: 'Lotes', icon: <InventarioIcon />, path: '/inventario/lotes' },
    ],
  },
  {
    id: 'promociones',
    label: 'Promociones',
    modulo: 'PROMOCIONES',
    icon: <VentasIcon />,
    children: [
      { id: 'promos', label: 'Promociones', icon: <VentasIcon />, path: '/promociones' },
      { id: 'campanas', label: 'Campañas', icon: <VentasIcon />, path: '/promociones/campanas' },
    ],
  },
  {
    id: 'ventas',
    label: 'Ventas',
    modulo: 'VENTAS',
    icon: <VentasIcon />,
    path: '/ventas',
  },
  {
    id: 'clientes',
    label: 'Clientes',
    modulo: 'CLIENTES',
    icon: <ClientesIcon />,
    path: '/clientes',
  },
  {
    id: 'credito',
    label: 'Crédito',
    modulo: 'CREDITO',
    icon: <AccountBalanceIcon />,
    permiso: 'CRED_VER_CARTERA',
    children: [
      { id: 'credito-dashboard',      label: 'Dashboard Cartera',  icon: <AccountBalanceIcon />, path: '/credito' },
      { id: 'credito-clientes',       label: 'Clientes con crédito', icon: <ClientesIcon />,     path: '/credito/clientes' },
      { id: 'credito-solicitudes',    label: 'Solicitudes',        icon: <ClientesIcon />,       path: '/credito/solicitudes' },
      { id: 'credito-cobro',          label: 'Gestión de Cobro',   icon: <ReportesIcon />,       path: '/credito/cobro' },
      { id: 'credito-comunicaciones', label: 'Comunicaciones',     icon: <FacturacionIcon />,    path: '/credito/comunicaciones' },
      { id: 'credito-config',         label: 'Configuración',      icon: <ConfigIcon />,         path: '/credito/configuracion' },
    ],
  },
  {
    id: 'domicilios',
    label: 'Domicilios',
    icon: <DomiciliosIcon />,
    path: '/domicilios',
    children: [
      { id: 'domicilios-despacho',     label: 'Panel de Despacho',  icon: <DomiciliosIcon />,     path: '/domicilios' },
      { id: 'domicilios-repartidores', label: 'Domiciliarios',      icon: <DomiciliarioIcon />,   path: '/domicilios/domiciliarios' },
    ],
  },
  {
    id: 'caja',
    label: 'Caja',
    modulo: 'CAJA',
    icon: <CajaIcon />,
    path: '/caja',
  },
  {
    id: 'reportes',
    label: 'Reportes',
    modulo: 'REPORTES',
    icon: <ReportesIcon />,
    path: '/reportes',
  },
  {
    id: 'administracion',
    label: 'Administración',
    icon: <AdminIcon />,
    roles: ['SuperAdmin', 'Admin'],
    children: [
      { id: 'administracion', label: 'Tiendas y Usuarios', icon: <EmpresaIcon />, path: '/administracion' },
      { id: 'tipo-negocio', label: 'Tipo de negocio y módulos', icon: <TipoNegocioIcon />, path: '/configuracion/negocio' },
      { id: 'importacion', label: 'Importación Masiva', icon: <ImportIcon />, path: '/importacion' },
      { id: 'catalogo-almacen', label: 'Catálogo por Almacén', icon: <CatalogoAlmacenIcon />, path: '/configuracion/catalogo-almacen' },
      { id: 'facturacion', label: 'Facturación DIAN', icon: <FacturacionIcon />, path: '/facturacion', modulo: 'FACTURACION' },
      { id: 'metodos-pago', label: 'Métodos de Pago', icon: <MetodosPagoIcon />, path: '/configuracion/metodos-pago' },
      { id: 'listas-precios', label: 'Listas de Precios', icon: <ListasPreciosIcon />, path: '/configuracion/listas-precios' },
      { id: 'perifericos', label: 'Periféricos', icon: <PerifericosIcon />, path: '/configuracion/perifericos' },
    ],
  },
];

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'temporary';
}

const Sidebar: React.FC<SidebarProps> = ({ 
  open = true, 
  onClose,
  variant = 'permanent' 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { sidebarCollapsed } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const { tieneModulo } = useNegocio();

  // Visible si el rol lo permite y la empresa tiene el módulo; un grupo sin hijos visibles se oculta
  const visibles = (items: MenuItem[]): MenuItem[] => items
    .filter(item => !item.roles || item.roles.some(r => user?.roles?.includes(r)))
    .filter(item => tieneModulo(item.modulo))
    .map(item => item.children ? { ...item, children: visibles(item.children) } : item)
    .filter(item => !item.children || item.children.length > 0);
  
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const handleToggleExpand = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (variant === 'temporary' && onClose) {
      onClose();
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    // Match exacto para evitar que el padre quede activo cuando un hijo está activo
    if (location.pathname === path) return true;
    // Match parcial solo si hay un separador después (evita que /credito matchee /credito/clientes incorrectamente como activo cuando hay un ítem hijo más específico)
    return false;
  };

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const active = isActive(item.path);

    const listItemContent = (
      <ListItemButton
        onClick={() => {
          if (hasChildren) {
            handleToggleExpand(item.id);
          } else if (item.path) {
            handleNavigate(item.path);
          }
        }}
        sx={{
          minHeight: 48,
          px: 2.5,
          pl: depth > 0 ? 4 : 2.5,
          borderRadius: 2,
          mx: 1,
          my: 0.5,
          ...(active && {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary.main,
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 40,
            color: active ? theme.palette.primary.main : 'inherit',
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!sidebarCollapsed && (
          <>
            <ListItemText 
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: active ? 600 : 500,
              }}
            />
            {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
          </>
        )}
      </ListItemButton>
    );

    return (
      <Box key={item.id}>
        {sidebarCollapsed ? (
          <Tooltip title={item.label} placement="right" arrow>
            {listItemContent}
          </Tooltip>
        ) : (
          listItemContent
        )}
        {hasChildren && !sidebarCollapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Logo y nombre */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          p: 2,
          minHeight: 64,
        }}
      >
        {!sidebarCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* <SucursalIcon sx={{ color: 'white', fontSize: 24 }} /> */}
              <img 
                src={iconImage} 
                alt="Icono sucursal" 
                style={{ 
                  width: 24, 
                  height: 24,
                  objectFit: 'contain', // Asegura que la imagen se ajuste sin distorsión
                  filter: 'brightness(0) saturate(100%) invert(1) grayscale(100%) contrast(1000%)'
                }} 
              />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary">
                POS
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.sucursalNombre || 'Sucursal'}
              </Typography>
            </Box>
          </Box>
        )}
        {sidebarCollapsed && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SucursalIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        )}
      </Box>

      <Divider sx={{ mx: 2 }} />

      {/* Menú principal */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          py: 1,
        }}
      >
        <List>
          {visibles(menuItems).map((item) => renderMenuItem(item))}
        </List>
      </Box>

      <Divider sx={{ mx: 2 }} />

      {/* Usuario y colapsar */}
      <Box sx={{ p: 2 }}>
        {!sidebarCollapsed && user && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
                fontSize: '0.875rem',
              }}
            >
              {user.nombre.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
              >
                {user.nombreCompleto}
              </Typography>
              <EmpresaActiva />
            </Box>
          </Box>
        )}

        {variant === 'permanent' && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton
              onClick={() => dispatch(toggleSidebarCollapsed())}
              size="small"
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: sidebarCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: sidebarCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: 'none',
          backgroundColor: theme.palette.background.paper,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;